// Phase −1 patches to this file:
//
// 1. Removed the client-supplied `ClinicId` header. The backend's
//    SecurityFilter now sets ClinicId from the validated JWT and ignores any
//    value the client sends. The header was always pointless; with the
//    SecurityFilter patched, sending it is also misleading — a code reviewer
//    seeing it might think it has meaning.
//
// 2. Fixed the broken bitwise-`&` operator in the error handler. The previous
//    expression was:
//        (error && error.status === 401) ||
//        error & (error.status === 500) ||
//        error & (error.status === 403)
//    The 500 and 403 branches used `&` (bitwise AND) instead of `&&` (logical
//    AND). With non-numeric operands, `error & anything` evaluates to 0
//    (falsy), so those branches never fired. The error toast and redirect-to-
//    login only ever ran on 401. Now all three statuses behave the same.
//
// 3. Defensive: the redirect uses replace() instead of href=, so the failed
//    request doesn't end up in browser history.

import { ajax } from 'rxjs/ajax'
import toast from 'react-hot-toast'
import { catchError } from 'rxjs/operators'
import { of } from 'rxjs'

const AUTH_FAILURE_STATUSES = new Set([401, 403, 500])

export function ajaxUtils(params) {
  const token = localStorage.getItem('access_token') || null
  const defaultHeader = {
    Authorization: token ? `Bearer ${token}` : undefined,
    'application-id': 2,
    'Content-Type': 'application/json',
    // NOTE: 'ClinicId' header intentionally NOT sent. The backend reads the
    // active clinic from the validated JWT claim, not from a client header.
  }
  // Strip undefined Authorization so we don't send "Authorization: undefined"
  if (!defaultHeader.Authorization) {
    delete defaultHeader.Authorization
  }

  const paramsHeader = params.headers || {}
  const passingHeader = token
    ? { ...defaultHeader, ...paramsHeader }
    : { ...paramsHeader }

  return ajax({ ...params, headers: passingHeader }).pipe(
    catchError((error) => {
      const status = error && error.status
      if (AUTH_FAILURE_STATUSES.has(status)) {
        toast.error('The connection has timed out. Please Try Again!')
        setTimeout(() => {
          // replace() so the failed URL isn't in the back button history
          window.location.replace('/login')
        }, 100)
      }
      return of(error)
    }),
  )
}

export function objectToUrl(url, object) {
  const encoded = Object.entries(object)
    .map(([key, value]) => `${key}=${value}`)
    .join('&')
    .replace(/[a-zA-Z0-9]+[=](null)(&)/g, '')
    .replace(/(&)[a-zA-Z0-9]+[=](null)/g, '')
  return `${url}?${encoded}`
}

export function objectToUrlTable(url, object) {
  const encoded = Object.entries(object)
    .map(([key, value]) => `${key}=${value}`)
    .join('&')
    .replace(/[a-zA-Z0-9]+[=](null)(&)/g, '')
    .replace(/(&)[a-zA-Z0-9]+[=](null)/g, '')
    .replace(/[a-zA-Z0-9]+[=]([\s]+)?(&)/g, '')
    .replace(/(&)[a-zA-Z0-9]+[=]+([\s]+)?(?![a-zA-Z0-9-])/g, '')
  return `${url}?${encoded}`
}
